import { QueryClient } from '@tanstack/react-query';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';

import type { CacheStorage } from './idb';
import { createIdbStorage } from './idb';

/**
 * TanStack Query's cache, persisted to IndexedDB
 * (https://tanstack.com/query/latest/docs/framework/react/plugins/persistQueryClient).
 *
 * `Persister` is the whole contract: three methods over one stored blob. The
 * storage is injected, so the tests drive it with `fake-indexeddb` and the apps
 * with the browser's own `indexedDB`.
 */

/** How long a restored cache stays usable before it is discarded. */
export const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Cache buster: bump it when a query's shape changes so old blobs are dropped
 * instead of hydrating into a component that can no longer read them.
 */
export const CACHE_BUSTER = 'bp-01';

/** A `Persister` over any `CacheStorage`. */
export function createPersister(storage: CacheStorage<PersistedClient>): Persister {
  return {
    persistClient: (client) => storage.setItem(client),
    restoreClient: () => storage.getItem(),
    removeClient: () => storage.removeItem(),
  };
}

/** The persister the apps use: the browser's IndexedDB. */
export function createIdbPersister(factory: IDBFactory): Persister {
  return createPersister(createIdbStorage<PersistedClient>(factory));
}

/**
 * One `QueryClient` per app.
 *
 * `retry: false` keeps the gate's e2e failing fast rather than spinning
 * (BP-00 R10); `gcTime` must outlive `MAX_AGE_MS`, or the cache is garbage
 * collected before it is ever persisted.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: false, refetchOnWindowFocus: false, gcTime: MAX_AGE_MS },
    },
  });
}
